import React from 'react';
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { ConfigField, validateExpression } from '../../../support/transformations/registry';

interface ConfigFieldFormProps {
  fields: ConfigField[];
  config: Record<string, any>;
  onChange: (next: Record<string, any>) => void;
  /** Column names detected from the selected sources, used to populate column pickers. */
  columns: string[];
}

/**
 * Renders a config form from a declarative op schema — no per-op hand-coded JSX. Column
 * pickers are populated from the data columns detected upstream, but stay free-text so an
 * author can reference a column that isn't in the preview sample. `expression` fields are
 * validated live against the sandboxed evaluator.
 */
const ConfigFieldForm: React.FC<ConfigFieldFormProps> = ({ fields, config, onChange, columns }) => {
  const set = (key: string, value: any) => onChange({ ...config, [key]: value });

  if (!fields.length) return null;

  return (
    <>
      {fields.map((field) => {
        const value = config[field.key] ?? field.default ?? '';
        const commonSx = { mb: 2 } as const;

        switch (field.type) {
          case 'column':
            return (
              <Autocomplete
                key={field.key}
                freeSolo
                options={columns}
                value={config[field.key] ?? null}
                onChange={(_e, v) => set(field.key, v ?? '')}
                onInputChange={(_e, v, reason) => { if (reason === 'input') set(field.key, v); }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={field.label}
                    required={field.required}
                    helperText={field.helperText}
                    placeholder={field.placeholder}
                  />
                )}
                sx={commonSx}
              />
            );

          case 'columns': {
            const arrVal: string[] = Array.isArray(config[field.key]) ? config[field.key] : [];
            return (
              <Autocomplete
                key={field.key}
                multiple
                freeSolo
                options={columns}
                value={arrVal}
                onChange={(_e, v) => set(field.key, v as string[])}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={field.label}
                    required={field.required}
                    helperText={field.helperText}
                    placeholder={field.placeholder}
                  />
                )}
                sx={commonSx}
              />
            );
          }

          case 'select':
            return (
              <FormControl fullWidth key={field.key} sx={commonSx} required={field.required}>
                <InputLabel>{field.label}</InputLabel>
                <Select
                  label={field.label}
                  value={value}
                  onChange={(e) => set(field.key, e.target.value)}
                >
                  {(field.options || []).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            );

          case 'boolean':
            return (
              <FormControlLabel
                key={field.key}
                sx={commonSx}
                control={
                  <Checkbox
                    checked={!!config[field.key]}
                    onChange={(e) => set(field.key, e.target.checked)}
                  />
                }
                label={field.label}
              />
            );

          case 'number':
            return (
              <TextField
                key={field.key}
                label={field.label}
                type="number"
                value={value}
                onChange={(e) => set(field.key, e.target.value === '' ? '' : Number(e.target.value))}
                fullWidth
                required={field.required}
                helperText={field.helperText}
                placeholder={field.placeholder}
                sx={commonSx}
              />
            );

          case 'expression': {
            const exprError = value ? validateExpression(String(value)) : null;
            return (
              <TextField
                key={field.key}
                label={field.label}
                value={value}
                onChange={(e) => set(field.key, e.target.value)}
                fullWidth
                multiline
                required={field.required}
                error={!!exprError}
                helperText={exprError || field.helperText}
                placeholder={field.placeholder}
                sx={{ ...commonSx, fontFamily: 'monospace' }}
                InputProps={{ sx: { fontFamily: 'monospace' } }}
              />
            );
          }

          case 'string':
          default:
            return (
              <TextField
                key={field.key}
                label={field.label}
                value={value}
                onChange={(e) => set(field.key, e.target.value)}
                fullWidth
                required={field.required}
                helperText={field.helperText}
                placeholder={field.placeholder}
                sx={commonSx}
              />
            );
        }
      })}
    </>
  );
};

export default ConfigFieldForm;
